import cx from 'clsx';

import {
	CardMerchantTable,
	MerchantCardList,
	ConfirmModal,
	PageSkeleton,
	LoginToPhonePeModal,
} from '@manage-cards/components/composed';
import { IMerchantCardData, IUserSummaryDetails } from '@manage-cards/types';
import { Global, Text } from '@manage-cards/constants';

import { Image } from '@shared/components/core';

import phonePeLogo from '@assets/icons/ppLogo.svg';

import { useManageCardsLayout } from './useManageCardsLayout';
import styles from './ManageCardsLayout.module.scss';

interface IManageCardsLayoutProps {
	isLoggedIn: boolean;
	isInitializing: boolean;
	userDetails: IUserSummaryDetails;
	loginViaPhonePe: () => void;
	logout: () => void;
}

const NoCardsFound = () => (
	<div className="d-flex flex-column justify-center items-center height100 border-radius4 bg-white">
		<Image
			src={Global.NO_CARDS_FOUND}
			alt="no-cards-found"
			width={177}
			height={177}
			className="mb4"
		/>
		<span className="h5 bold dark3">{Text.NO_CARDS_FOUND}</span>
	</div>
);

const ManageCardsLayout = ({
	isLoggedIn,
	isInitializing,
	userDetails,
	loginViaPhonePe,
	logout,
}: IManageCardsLayoutProps) => {
	const {
		loading,
		filterType,
		showConfirmModal,
		cardDataToDelete,
		setFilterType,
		optionItemList,
		onOptionItemSelect,
		currentSelectedData,
		handleCardDelete,
		setShowConfirmModal,
		showDeleteConfirmation,
	} = useManageCardsLayout({ isLoggedIn, isInitializing });

	const merchantId =
		(currentSelectedData as IMerchantCardData)?.merchantId ?? '';

	const renderTable = () => {
		if (!currentSelectedData?.savedCards?.length) {
			return <NoCardsFound />;
		}
		return (
			<CardMerchantTable
				tableData={currentSelectedData}
				onCardDelete={showDeleteConfirmation}
				merchantId={merchantId}
				filterType={filterType}
			/>
		);
	};

	const renderContent = () => {
		if (loading || isInitializing) {
			return <PageSkeleton />;
		}
		if (!isLoggedIn) {
			return (
				<>
					<LoginToPhonePeModal handleLogin={loginViaPhonePe} />
					<PageSkeleton />
				</>
			);
		}
		return (
			<section className={cx(styles.contentSection, 'd-flex')}>
				<div
					className={cx(
						styles.cardOptionListContainer,
						'bg-white d-flex flex-column',
					)}
				>
					<MerchantCardList
						userDetails={userDetails}
						itemList={optionItemList}
						onItemSelect={onOptionItemSelect}
						filterType={filterType}
						onFilterChange={(type) => setFilterType(type)}
						logout={logout}
					/>
				</div>
				<div className={cx(styles.listContainer, 'p4')}>{renderTable()}</div>
			</section>
		);
	};

	return (
		<main className="d-flex justify-center items-center height100 width100">
			<div
				className={cx(
					styles.manageCardsContainer,
					'border-radius6 height100 width100 border1 border-grey1 bg-secondary overflow-hidden',
				)}
			>
				<div className="d-flex justify-between items-center px6 py3 border-bottom1 border-grey2 bg-white">
					<Image
						src={phonePeLogo}
						className={styles.brandLogo}
						alt="PhonePe"
						loading="eager"
					/>
					<span className="h5 bold dark3"> {Text.SAVED_CARDS} </span>
					<span />
				</div>
				{renderContent()}
			</div>
			{showConfirmModal && (
				<ConfirmModal
					handleCancel={() => setShowConfirmModal(false)}
					handleConfirm={handleCardDelete}
					status={cardDataToDelete.status}
				/>
			)}
		</main>
	);
};

export default ManageCardsLayout;
